import { FC, useEffect } from 'react';
import { Grid, Typography, Divider } from '@mui/material';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import StructuredModal from '../../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../../components/controlledComponents/ControlledSelectField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import {
  CreateConcernPayload,
} from '../../../../../../apis/messaging/messaging.interfaces';

interface RaiseConcernFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateConcernPayload) => void;
  isLoading?: boolean;
  errorMessage?: string;
  instituteRef?: string;
  studentId?: string;
}

const getConcernFormSchema = (translateText: TTranslateTextType) =>
  z.object({
    subject: z
      .string()
      .min(1, translateText('communications.concerns.validation.subjectRequired'))
      .max(200, translateText('communications.concerns.validation.subjectMaxLength')),
    description: z
      .string()
      .min(1, translateText('communications.concerns.validation.descriptionRequired')),
    category: z.enum([
      'academic',
      'behavior',
      'attendance',
      'health',
      'transport',
      'fees',
      'other',
    ]),
    priority: z.enum(['low', 'medium', 'high']),
  });

type ConcernFormData = CreateConcernPayload;

const categoryOptions = [
  { key: 'academic', value: 'academic' },
  { key: 'behavior', value: 'behavior' },
  { key: 'attendance', value: 'attendance' },
  { key: 'health', value: 'health' },
  { key: 'transport', value: 'transport' },
  { key: 'fees', value: 'fees' },
  { key: 'other', value: 'other' },
];

const priorityOptions = [
  { key: 'low', value: 'low' },
  { key: 'medium', value: 'medium' },
  { key: 'high', value: 'high' },
];

const RaiseConcernForm: FC<RaiseConcernFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  errorMessage,
  instituteRef = 'inst1',
  studentId,
}) => {
  const { translateText } = useTranslation();
  const schema = getConcernFormSchema(translateText);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ConcernFormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      subject: '',
      description: '',
      category: 'academic',
      priority: 'medium',
      institute_ref: instituteRef,
    },
  });

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const onSubmitForm = (data: ConcernFormData) => {
    onSubmit({
      ...data,
      institute_ref: instituteRef,
      student_id: studentId,
    });
  };

  return (
    <StructuredModal
      isOpen={isOpen}
      onClose={onClose}
      title={translateText('communications.concerns.raiseConcern')}
      footerPositiveAction={handleSubmit(onSubmitForm)}
      footerPositiveActionText={translateText('communications.concerns.submit')}
      footerNegativeAction={onClose}
      footerNegativeActionText={translateText('common.cancel')}
      footerMessage={
        errorMessage ? (
          <Typography color="error" variant="body2">
            {errorMessage}
          </Typography>
        ) : null
      }
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit(onSubmitForm)} style={{ width: '100%' }}>
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ p: { xs: 1, sm: 2 } }}
          data-testid="raise-concern-form-container"
        >
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {translateText('communications.concerns.formTitle')}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField
              id="subject"
              label={translateText('communications.concerns.subject')}
              name="subject"
              control={control}
              errorMessage={errors.subject?.message}
              multiline={false}
              dataTestId="concern-subject-input"
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField
              id="description"
              label={translateText('communications.concerns.description')}
              name="description"
              control={control}
              errorMessage={errors.description?.message}
              multiline
              rows={4}
              dataTestId="concern-description-input"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledSelectField
              id="category"
              label={translateText('communications.concerns.category')}
              name="category"
              control={control}
              itemList={categoryOptions.map((o) => ({
                key: translateText(`communications.concerns.categories.${o.key}`),
                value: o.value,
              }))}
              dataTestId="concern-category-select"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledSelectField
              id="priority"
              label={translateText('communications.concerns.priority')}
              name="priority"
              control={control}
              itemList={priorityOptions.map((o) => ({
                key: translateText(`communications.concerns.priorities.${o.key}`),
                value: o.value,
              }))}
              dataTestId="concern-priority-select"
            />
          </Grid>
        </Grid>
      </form>
    </StructuredModal>
  );
};

export default RaiseConcernForm;
